var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var _gql;
import { ErrorInvalidNetwork, ErrorNotFound, ErrorUnexpectedResult, toNetwork, } from '@1hive/connect-core';
import * as queries from './queries';
import GraphQLWrapper from './core/GraphQLWrapper';
import { parseApp, parseApps, parsePermissions, parseRepo, parseRoles, } from './parsers';
function getOrgSubgraphUrl(network) {
    if (network.chainId === 1) {
        return 'https://api.thegraph.com/subgraphs/name/aragon/aragon-mainnet';
    }
    if (network.chainId === 4) {
        return 'https://api.thegraph.com/subgraphs/name/aragon/aragon-rinkeby';
    }
    if (network.chainId === 100) {
        return 'https://api.thegraph.com/subgraphs/name/1hive/aragon-xdai';
    }
    return null;
}
function appFiltersToQueryFilter(appFilters) {
    const queryFilter = {};
    if (appFilters.name) {
        queryFilter.repoName_in = appFilters.name.map((name) => name.replace(/\.aragonpm\.eth$/, ''));
    }
    if (appFilters.address) {
        queryFilter.address_in = appFilters.address;
    }
    return queryFilter;
}
class ConnectorTheGraph {
    constructor(config) {
        _gql.set(this, void 0);
        this.name = 'thegraph';
        this.config = config;
        this.network = toNetwork(config.network);
        const orgSubgraphUrl = config.orgSubgraphUrl || getOrgSubgraphUrl(this.network);
        if (!orgSubgraphUrl) {
            throw new ErrorInvalidNetwork(`The chainId ${this.network.chainId} is not supported by the TheGraph connector.`);
        }
        __classPrivateFieldSet(this, _gql, new GraphQLWrapper(orgSubgraphUrl, {
            pollInterval: config.pollInterval,
            verbose: config.verbose,
        }));
    }
    async connect(connection) {
        this.connection = connection;
    }
    async disconnect() {
        __classPrivateFieldGet(this, _gql).close();
        delete this.connection;
    }
    async rolesForAddress(organization, appAddress) {
        try {
            return __classPrivateFieldGet(this, _gql).performQueryWithParser(queries.ROLE_BY_APP_ADDRESS('query'), { appAddress: appAddress.toLowerCase() }, async (result) => parseRoles(result, organization));
        }
        catch (err) {
            throw new ErrorUnexpectedResult('Unexpected result when fetching the roles.');
        }
    }
    async permissionsForOrg(organization) {
        try {
            return __classPrivateFieldGet(this, _gql).performQueryWithParser(queries.ORGANIZATION_PERMISSIONS('query'), { orgAddress: organization.address.toLowerCase() }, (result) => parsePermissions(result, organization));
        }
        catch (err) {
            throw new ErrorUnexpectedResult('Unexpected result when fetching the permissions.');
        }
    }
    onPermissionsForOrg(organization, callback) {
        return __classPrivateFieldGet(this, _gql).subscribeToQueryWithParser(queries.ORGANIZATION_PERMISSIONS('subscription'), { orgAddress: organization.address.toLowerCase() }, callback, async (result) => {
            try {
                return await parsePermissions(result, organization);
            }
            catch (err) {
                throw new ErrorUnexpectedResult('Unexpected result when fetching the permissions.');
            }
        });
    }
    async appByAddress(organization, appAddress) {
        try {
            return __classPrivateFieldGet(this, _gql).performQueryWithParser(queries.APP_BY_ADDRESS('query'), { appAddress: appAddress.toLowerCase() }, (result) => parseApp(result, organization));
        }
        catch (err) {
            if (err instanceof ErrorNotFound) {
                throw new ErrorNotFound('No app found with the current filters.');
            }
            throw new ErrorUnexpectedResult('Unexpected result when fetching the app.');
        }
    }
    async appForOrg(organization, filters) {
        try {
            const apps = await __classPrivateFieldGet(this, _gql).performQueryWithParser(queries.ORGANIZATION_APPS('query'), {
                appFilter: appFiltersToQueryFilter(filters),
                first: 1,
                orgAddress: organization.address.toLowerCase(),
            }, async (result) => parseApps(result, organization));
            return apps[0];
        }
        catch (err) {
            if (err instanceof ErrorNotFound) {
                throw new ErrorNotFound('No app found with the current filters.');
            }
            throw new ErrorUnexpectedResult('Unexpected result when fetching the app.');
        }
    }
    onAppForOrg(organization, filters, callback) {
        return __classPrivateFieldGet(this, _gql).subscribeToQueryWithParser(queries.ORGANIZATION_APPS('subscription'), {
            appFilter: appFiltersToQueryFilter(filters),
            first: 1,
            orgAddress: organization.address.toLowerCase(),
        }, callback, async (result) => {
            try {
                const apps = await parseApps(result, organization);
                if (!apps[0]) {
                    throw new ErrorNotFound();
                }
                return apps[0];
            }
            catch (err) {
                if (err instanceof ErrorNotFound) {
                    throw new ErrorNotFound('No app found with the current filters.');
                }
                throw new ErrorUnexpectedResult('Unexpected result when fetching the app.');
            }
        });
    }
    async appsForOrg(organization, filters) {
        return __classPrivateFieldGet(this, _gql).performQueryWithParser(queries.ORGANIZATION_APPS('query'), {
            appFilter: appFiltersToQueryFilter(filters),
            orgAddress: organization.address.toLowerCase(),
        }, async (result) => {
            try {
                return await parseApps(result, organization);
            }
            catch (err) {
                throw new ErrorUnexpectedResult('Unexpected result when fetching the apps.');
            }
        });
    }
    onAppsForOrg(organization, filters, callback) {
        return __classPrivateFieldGet(this, _gql).subscribeToQueryWithParser(queries.ORGANIZATION_APPS('subscription'), {
            appFilter: appFiltersToQueryFilter(filters),
            orgAddress: organization.address.toLowerCase(),
        }, callback, async (result) => {
            try {
                return await parseApps(result, organization);
            }
            catch (err) {
                throw new ErrorUnexpectedResult('Unexpected result when fetching the apps.');
            }
        });
    }
    async repoForApp(organization, appAddress) {
        return __classPrivateFieldGet(this, _gql).performQueryWithParser(queries.REPO_BY_APP_ADDRESS('query'), { appAddress: appAddress.toLowerCase() }, async (result) => {
            try {
                return await parseRepo(result, organization);
            }
            catch (err) {
                if (err instanceof ErrorNotFound) {
                    throw new ErrorNotFound('The app repo wasn’t found.');
                }
                throw new ErrorUnexpectedResult('Unexpected result when fetching the app repo.');
            }
        });
    }
}
_gql = new WeakMap();
export default ConnectorTheGraph;
//# sourceMappingURL=connector.js.map